import { Form, Input, Switch } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';

import { apiFetch } from '@/api/client';
import {
  financialYearsKey,
  useFinancialYears,
  type FinancialYear,
  type FinancialYearStatus,
} from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { DatePicker } from '@/components/DatePicker';
import { FormDrawer } from '@/components/FormDrawer';
import { IconAction } from '@/components/IconAction';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { StatusBadge, type StatusTone } from '@/components/StatusBadge';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import { formatDate, formatDateTime, suggestFinancialYear, toIsoDate } from '@core/format/dates';
import type { Session } from '@core/session/types';

const statusLabel: Record<FinancialYearStatus, { text: string; tone: StatusTone }> = {
  Open: { text: 'Open', tone: 'positive' },
  SoftClosed: { text: 'Soft-closed', tone: 'warning' },
  Closed: { text: 'Closed', tone: 'neutral' },
};

type Editing = { mode: 'create' } | { mode: 'edit'; year: FinancialYear };

/**
 * FY Master and its close lifecycle (spec Section 15).
 *
 * Two rules from the service are visible in what this screen offers. Soft-close and hard-close are
 * different acts: the first locks most people out while the accountant finishes posting
 * corrections, the second is final and cannot be undone by anyone, which is precisely the
 * guarantee an auditor relies on. So a hard close is asked about in those words, and a closed year
 * offers nothing afterwards — no edit, no reopen.
 *
 * "Current" is also not the same as "open". Several years can be open at once during a year-end
 * overlap; exactly one is the year new documents default to.
 */
export function FinancialYearsPage({ session }: { session: Session }) {
  const [editing, setEditing] = useState<Editing | null>(null);

  const years = useFinancialYears();

  const transition = useApiMutation<
    FinancialYear,
    { year: FinancialYear; action: 'soft-close' | 'close' | 'reopen'; done: string }
  >({
    mutationFn: ({ year, action }) =>
      apiFetch<FinancialYear>(`/api/financial-year/${year.id}/${action}`, { method: 'POST' }),
    success: (_result, input) => input.done,
    invalidates: [financialYearsKey],
  });

  const run = (input: {
    year: FinancialYear;
    action: 'soft-close' | 'close' | 'reopen';
    done: string;
  }) => void transition.mutateAsync(input).catch(() => undefined);

  const columns: DataViewColumn<FinancialYear>[] = [
    {
      title: 'Year',
      dataIndex: 'code',
      width: 150,
      primary: true,
      render: (_, row) => (
        <span className="inline-flex items-center gap-2">
          <span className="font-mono-nums font-semibold">{row.code}</span>
          {row.isCurrent && <StatusBadge tone="brand">Current</StatusBadge>}
        </span>
      ),
    },
    {
      title: 'From',
      dataIndex: 'startDate',
      width: 120,
      render: (_, row) => <span className="font-mono-nums">{formatDate(row.startDate)}</span>,
    },
    {
      title: 'To',
      dataIndex: 'endDate',
      width: 120,
      render: (_, row) => <span className="font-mono-nums">{formatDate(row.endDate)}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (_, row) => (
        <StatusBadge tone={statusLabel[row.status].tone}>
          {statusLabel[row.status].text}
        </StatusBadge>
      ),
    },
    {
      title: 'Closed',
      dataIndex: 'closedOn',
      width: 150,
      responsive: ['xl'],
      render: (_, row) => (
        <span className="text-[12px] text-default">{formatDateTime(row.closedOn)}</span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 190,
      trailing: true,
      align: 'right',
      fixed: 'right',
      render: (_, row) => {
        const closed = row.status === 'Closed';
        const finalised = closed ? 'A closed year is final' : undefined;

        return (
          <div className="flex items-center justify-end gap-1">
            <RecordAction session={session} required="FinancialYear:Edit" forbidden={finalised}>
              {(disabled) => (
                <IconAction
                  icon="icon icon-pencil-line"
                  label={`Edit ${row.code}`}
                  disabled={disabled}
                  onClick={() => setEditing({ mode: 'edit', year: row })}
                />
              )}
            </RecordAction>

            {/*
              Soft close and reopen are the same slot because they are the same decision seen from
              either side. Showing both at once would offer an operator a reopen on a year that is
              still open, which is not a state anything can be in.
            */}
            {row.status === 'SoftClosed' ? (
              <RecordAction session={session} required="FinancialYear:Approve">
                {(disabled) => (
                  <Button
                    disabled={disabled}
                    onClick={() =>
                      run({ year: row, action: 'reopen', done: `${row.code} reopened.` })
                    }
                  >
                    Reopen
                  </Button>
                )}
              </RecordAction>
            ) : (
              <RecordAction
                session={session}
                required="FinancialYear:Approve"
                forbidden={finalised}
              >
                {(disabled) => (
                  <Button
                    disabled={disabled}
                    onClick={() =>
                      run({ year: row, action: 'soft-close', done: `${row.code} soft-closed.` })
                    }
                  >
                    Soft close
                  </Button>
                )}
              </RecordAction>
            )}

            <RecordAction session={session} required="FinancialYear:Approve" forbidden={finalised}>
              {(disabled) => (
                <ConfirmButton
                  icon="icon icon-lock"
                  label={`Close ${row.code} permanently`}
                  title={`Close ${row.code} for good?`}
                  description="Nobody can reopen it or post into it afterwards — not an administrator, not support."
                  confirmText="Close the year"
                  disabled={disabled}
                  pending={transition.isPending}
                  onConfirm={() => run({ year: row, action: 'close', done: `${row.code} closed.` })}
                />
              )}
            </RecordAction>
          </div>
        );
      },
    },
  ];

  if (years.isError) {
    return <ApiErrorAlert error={years.error} />;
  }

  return (
    <>
      <PageHeading
        title="Financial years"
        subtitle="The year a document belongs to, and the point after which it can no longer change."
        actions={
          <WriteAction session={session} required="FinancialYear:Create">
            {(disabled) => (
              <Button
                variant="primary"
                icon="icon icon-plus"
                shortcut="create"
                disabled={disabled}
                onClick={() => setEditing({ mode: 'create' })}
              >
                New year
              </Button>
            )}
          </WriteAction>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <DataView<FinancialYear>
          columns={columns}
          dataSource={years.data ?? []}
          loading={years.isLoading}
          empty={
            <EmptyState
              icon="icon icon-calendar-range"
              title="No financial years yet"
              hint="Nothing can be numbered until a year exists — every document carries the year it belongs to."
            />
          }
        />
      </Card>

      {editing && <FinancialYearDrawer editing={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

/**
 * `startDate` and `endDate` are Dayjs here rather than strings, because that is what antd's
 * `DatePicker` both produces and expects back as an initial value. `toIsoDate` accepts either and
 * formats the *picked* date rather than converting through UTC — which would turn the 1st of April
 * into the 31st of March for anyone east of Greenwich, on the one field where that matters most.
 */
interface FinancialYearFormValues {
  code: string;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  isCurrent: boolean;
}

function FinancialYearDrawer({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const [form] = Form.useForm<FinancialYearFormValues>();
  const isEdit = editing.mode === 'edit';
  const existing = editing.mode === 'edit' ? editing.year : undefined;

  const save = useApiMutation<FinancialYear, FinancialYearFormValues>({
    mutationFn: (values) => {
      const body = {
        code: values.code,
        startDate: toIsoDate(values.startDate),
        endDate: toIsoDate(values.endDate),
        isCurrent: values.isCurrent ?? false,
      };

      return isEdit
        ? apiFetch<FinancialYear>(`/api/financial-year/${existing?.id}`, {
            method: 'PUT',
            body,
          })
        : apiFetch<FinancialYear>('/api/financial-year', { method: 'POST', body });
    },
    success: isEdit ? 'Financial year updated.' : 'Financial year created.',
    invalidates: [financialYearsKey],
    onSuccess: onClose,
  });

  // The statutory year is the same answer every time, so it is offered rather than typed — a year
  // that starts on the wrong day is not obviously wrong until documents are numbered into it.
  const suggested = suggestFinancialYear();

  const initialValues: FinancialYearFormValues = existing
    ? {
        code: existing.code,
        startDate: dayjs(existing.startDate.slice(0, 10)),
        endDate: dayjs(existing.endDate.slice(0, 10)),
        isCurrent: existing.isCurrent,
      }
    : {
        code: suggested.code,
        startDate: dayjs(suggested.startDate),
        endDate: dayjs(suggested.endDate),
        isCurrent: false,
      };

  return (
    <FormDrawer<FinancialYearFormValues>
      title={isEdit ? `Edit ${existing?.code ?? 'year'}` : 'New financial year'}
      open
      onClose={onClose}
      form={form}
      initialValues={initialValues}
      onSubmit={(values) => void submitVia(save, values)}
      submitText={isEdit ? 'Save changes' : 'Create year'}
      submitting={save.isPending}
      width={460}
    >
      <Form.Item
        name="code"
        label="Code"
        extra="Appears inside every document number issued in this year."
        rules={[{ required: true, message: 'A code is required' }]}
      >
        <Input className="font-mono-nums" />
      </Form.Item>

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item
          name="startDate"
          label="From"
          rules={[{ required: true, message: 'A start date is required' }]}
        >
          <DatePicker />
        </Form.Item>

        <Form.Item
          name="endDate"
          label="To"
          rules={[{ required: true, message: 'An end date is required' }]}
        >
          <DatePicker />
        </Form.Item>
      </div>

      <Form.Item
        name="isCurrent"
        label="Current year"
        valuePropName="checked"
        extra="The year new documents default to. Turning it on here turns it off elsewhere."
      >
        <Switch size="small" />
      </Form.Item>

      <div className="mt-2 rounded-md border border-border-color bg-light p-3">
        <p className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
          <i className="icon icon-info text-info" />
          Years may not overlap
        </p>
        <p className="mt-1 text-[13px] text-default">
          Every transaction date has to resolve to exactly one year, or numbering and reporting
          become ambiguous. The server refuses an overlapping range.
        </p>
        <p className="mt-2 text-[12px] text-default">
          Each branch gets its own numbering series inside a new year, starting again at one.
          Nothing needs resetting — a new year simply has new counters.
        </p>
      </div>
    </FormDrawer>
  );
}
